<template>
  <el-dialog
    v-model="showDialog"
    :width="width"
    align-center
    :show-close="false"
    :close-on-click-modal="false"
    class="confirm-dialog"
  >
    <div class="grid justify-items-center" style="padding: 16px 8px;">
      <div class="mb-[24px]">
        <icon-svg :name="iconPopup" />
      </div>
      <div class="text-xl font-medium mb-[16px]">{{ title }}</div>
      <p class="text-base font-bold" style="word-break: break-word; text-align: center; white-space: pre-line; padding: 0 10px;">
        {{ message }}
      </p>
      <slot name="content" />
    </div>
    <template #footer>
      <div class="flex justify-center" style="padding: 16px 8px;">
        <el-button 
          :class="['justify-self-center w-[112px] ml-[16px]', cancelType === 'danger' ? 'btn-cancel-danger' : 'btn-cancel']" 
          @click="handleCancel"
        >
          {{ lblBtnCancel || 'Cancel' }}
        </el-button>
        <el-button 
          v-if="lblBtnSubmit" 
          class="justify-self-center delete mr-0 btn-confirm pl-[32px] ml-[16px] pr-[32px] w-[154px]" 
          @click="handleSubmit"
        >
          <el-icon v-if="iconBtnName">
            <icon-svg width="10" height="10" :name="iconBtnName" />
          </el-icon>
          <span class="ml-[6px] font-bold">{{ lblBtnSubmit }}</span>
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { ref, watch } from "vue";
import IconSvg from '@/layouts/IconBase.vue';

interface Props {
  title?: string;
  message?: string;
  lblBtnSubmit?: string;
  lblBtnCancel?: string;
  iconBtnName?: string;
  iconPopup?: string;
  width?: number;
  visible?: boolean;
  cancelType?: 'default' | 'danger';
  autoCloseOnSubmit?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Confirmation',
  message: 'Are you sure?',
  lblBtnSubmit: 'Continue',
  lblBtnCancel: 'Cancel',
  iconBtnName: 'check-mark-circle',
  iconPopup: 'warring',
  width: 352,
  visible: false,
  cancelType: 'default',
  autoCloseOnSubmit: true
});

const emit = defineEmits<{
  (e: 'submit'): void;
  (e: 'cancel'): void;
  (e: 'update:visible', value: boolean): void;
}>();

const showDialog = ref<boolean>(props.visible);

watch(() => props.visible, (newVal) => {
  showDialog.value = newVal;
});

watch(showDialog, (newVal) => {
  emit('update:visible', newVal);
});

const handleSubmit = () => {
  emit('submit');
  if (props.autoCloseOnSubmit) {
    showDialog.value = false;
  }
};

const handleCancel = () => {
  emit('cancel');
  showDialog.value = false;
};
</script>

<style lang="scss" scoped>
.confirm-dialog {
  .btn-confirm {
    background-color: #246bfd;
    border-color: #246bfd;
    color: white;
    
    &:hover,
    &:focus {
      background-color: #1e5ae8;
      border-color: #1e5ae8;
    }
  }
  
  .btn-cancel {
    background-color: white;
    border-color: #dcdfe6;
    color: #606266;
    
    &:hover,
    &:focus {
      background-color: #f5f7fa;
      border-color: #c0c4cc;
    }
  }
  
  .btn-cancel-danger {
    background-color: #ed1c2d !important;
    border-color: #ed1c2d !important;
    color: white !important;
    
    &:hover,
    &:focus {
      background-color: #d41825;
      border-color: #d41825;
    }
  }
}
</style>
